import { hasValue } from 'Lib/Any';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { GroupBase, components } from 'react-select';
import Creatable from 'react-select/creatable';
import Select from 'react-select/dist/declarations/src/Select';
import { withInjectedProps } from '../InjectedPropsContext';
import './Dropdown.scss';
import { defaultFilterOption } from './filters';
import {
  DropdownWrapper,
  ISharedDropdownProps,
  toListItemValue,
  useChangeHandler,
  useKeepMenuUnderDropdownEffect,
} from './shared';

export type ICreatableDropdownProps = ISharedDropdownProps & {
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
} & Partial<Pick<HTMLInputElement, 'maxLength'>>;

const CreatableDropdownBase: React.FC<ICreatableDropdownProps> = (props) => {
  const {
    defaultValue,
    disabled,
    filterOption = defaultFilterOption,
    formatCreateLabel,
    id,
    isLoading,
    maxLength,
    maxResults = Number.MAX_SAFE_INTEGER,
    onInputChange,
    onMenuOpen,
    options = [],
    placeholder = 'Select or enter a new option...',
    severity,
  } = props;

  const cx = classNames('Dropdown', { [`Dropdown-${severity}`]: !!severity });

  /*
   * Transform the value to an appropriate type.
   * (We either want an option or a simple value.)
   */
  const handleChange = useChangeHandler(props);
  const dropdownValue = toListItemValue(props);

  /*
   * Keep the dropdown menu positioned underneath the dropdown when the viewport is scrolled.
   */
  const selectRef = useRef<Select<any, false, GroupBase<any>> | null>(null);
  const menuPortalRef = useRef<any>(null);
  const MenuPortal = (props: any) => <components.MenuPortal {...props} innerRef={menuPortalRef} />;

  useKeepMenuUnderDropdownEffect(selectRef, menuPortalRef);

  useEffect(() => {
    if (hasValue(selectRef.current?.inputRef)) {
      if (hasValue(maxLength)) {
        selectRef.current!.inputRef!.maxLength = maxLength!;
      } else {
        selectRef.current!.inputRef!.removeAttribute('maxLength');
      }
    }
  }, [selectRef, maxLength]);

  return (
    <DropdownWrapper disabled={disabled}>
      <Creatable
        allowCreateWhileLoading={true}
        className={cx}
        classNamePrefix="Dropdown"
        components={{ MenuPortal }}
        defaultValue={defaultValue as any}
        filterOption={filterOption}
        formatCreateLabel={formatCreateLabel}
        inputId={id}
        inputValue={undefined}
        isClearable
        isDisabled={disabled}
        isLoading={isLoading}
        isSearchable={true}
        loadingMessage={() => 'Searching...'}
        menuPortalTarget={document.body}
        noOptionsMessage={() => (options.length === 0 ? 'Start typing to search.' : 'No matches.')}
        onChange={handleChange}
        onInputChange={onInputChange}
        onMenuOpen={onMenuOpen}
        options={options.slice(0, maxResults)}
        placeholder={isLoading ? 'Loading...' : placeholder !== undefined ? placeholder : undefined}
        ref={(reactSelect) => {
          selectRef.current = reactSelect;
        }}
        /*
         * Styles can't be directly applied to the portal element because it doesn't use the classNamePrefix prop.
         * https://github.com/JedWatson/react-select/issues/2909
         */
        styles={{ menuPortal: (styles) => ({ ...styles, zIndex: 9999 }) }}
        value={dropdownValue}
      />
    </DropdownWrapper>
  );
};

const CreatableDropdown = withInjectedProps(CreatableDropdownBase);
export { CreatableDropdown };
